import type { SpaceOverlayModel, SpaceOverlaySection } from '@toolproof-core/visualization/runtime/_contract/overlay';

import type { EpisodeSummary } from '@/_contract/episode';
import type { SessionMode } from '@/drivers/nativeAdapterDriver/_contract/session';

export const SESSION_SECTION_ID = 'cosmos-session-mode';
export const EPISODE_SECTION_ID = 'cosmos-session-episode';
export const RESUME_EPISODE_ACTION = 'cosmos-resume-episode';
export const KEEP_EXPLORING_ACTION = 'cosmos-keep-exploring';
export const SESSION_MODE_ACTIONS: Readonly<Record<SessionMode, string>> = {
    exploration: 'cosmos-session-exploration',
    demonstration: 'cosmos-session-demonstration',
};

export function sessionModeForAction(actionId: string): SessionMode | undefined {
    return (Object.keys(SESSION_MODE_ACTIONS) as SessionMode[])
        .find((mode) => SESSION_MODE_ACTIONS[mode] === actionId);
}

export function withSessionOverlay(args: {
    model: SpaceOverlayModel;
    sessionMode: SessionMode;
    episodes: readonly EpisodeSummary[];
    episodeHandle?: string;
    choosingEpisode: boolean;
    confirmRestore: boolean;
    busy: boolean;
    available: boolean;
    adapterSectionId?: string;
}): SpaceOverlayModel {
    const selectedMode: SessionMode = args.choosingEpisode ? 'demonstration' : args.sessionMode;
    const modeEnabled = !args.busy && args.available;
    const sections: SpaceOverlaySection[] = [{
        sectionKind: 'actions', sectionId: SESSION_SECTION_ID, label: 'Session',
        actions: [
            { actionId: SESSION_MODE_ACTIONS.exploration, label: 'Exploration',
                enabled: modeEnabled, isActive: selectedMode === 'exploration' },
            { actionId: SESSION_MODE_ACTIONS.demonstration, label: 'Demonstration',
                enabled: modeEnabled, isActive: selectedMode === 'demonstration' },
        ],
    }];
    if (args.sessionMode === 'demonstration' || args.choosingEpisode) {
        sections.push({
            sectionKind: 'select', sectionId: EPISODE_SECTION_ID, label: 'Episode',
            selectedValue: args.sessionMode === 'demonstration' ? args.episodeHandle ?? '' : '',
            enabled: args.available,
            allowDuringPlayback: true,
            options: [{ value: '', label: 'Choose an episode' },
                ...args.episodes.map((episode) => ({ value: episode.handle, label: episode.title }))],
        });
    }
    if (args.confirmRestore && args.episodeHandle && args.sessionMode === 'exploration') {
        sections.push({
            sectionKind: 'actions', sectionId: 'cosmos-checkpoint-confirmation', label: 'Resume episode?',
            description: 'Resuming this Episode will restore its saved inputs and playback position.',
            actions: [
                {
                    actionId: RESUME_EPISODE_ACTION,
                    label: 'Restore and resume',
                    enabled: !args.busy && args.available,
                },
                { actionId: KEEP_EXPLORING_ACTION, label: 'Keep exploring', enabled: !args.busy },
            ],
        });
    }
    if (!args.available) {
        sections.push({
            sectionKind: 'actions', sectionId: 'cosmos-episode-availability', label: 'Episode availability',
            description: 'Episodes are not available in this adapter mode.', actions: [],
        });
    }
    const adapter = args.model.sections.find((section) => section.sectionId === args.adapterSectionId);
    return { ...args.model, sections: [
        ...(adapter ? [{ ...adapter, ...(args.sessionMode === 'demonstration' ? { enabled: false } : {}) }] : []),
        ...sections,
        ...(args.choosingEpisode ? [] : args.model.sections.filter((section) => section !== adapter)),
    ] };
}
